'use client'

import { cn } from '@/lib/utils'
import React from 'react'

interface ProgressiveBlurProps {
  blur?: string
  ratio?: number
  className?: string
  overrideDefaults?: boolean
}

const ProgressiveBlur: React.FC<ProgressiveBlurProps> = ({ 
  blur = '1rem', 
  ratio = 2,
  className = '',
  overrideDefaults = false,
}) => {
  const panels = [
    {
      id: 1,
      blurPower: 0, // No division, just use --blur
      mask: 'linear-gradient(#000 0% 10%, #0000 30%)',
      webkitMask: 'linear-gradient(#000 0% 10%, #0000 30%)'
    },
    {
      id: 2,
      blurPower: 1, // Divide by ratio once
      mask: 'linear-gradient(#0000 0%, #000 10% 20%, #0000 40%)',
      webkitMask: 'linear-gradient(#0000 0%, #000 10% 20%, #0000 40%)'
    },
    {
      id: 3,
      blurPower: 2,
      mask: 'linear-gradient(#0000 0%, #000 20% 30%, #0000 50%)',
      webkitMask: 'linear-gradient(#0000 0%, #000 20% 30%, #0000 50%)'
    },
    {
      id: 4,
      blurPower: 3,
      mask: 'linear-gradient(#0000 10%, #000 30% 40%, #0000 60%)',
      webkitMask: 'linear-gradient(#0000 10%, #000 30% 40%, #0000 60%)'
    },
    {
      id: 5,
      blurPower: 4,
      mask: 'linear-gradient(#0000 20%, #000 40% 50%, #0000 70%)',
      webkitMask: 'linear-gradient(#0000 20%, #000 40% 50%, #0000 70%)'
    },
    {
      id: 6,
      blurPower: 5,
      mask: 'linear-gradient(#0000 30%, #000 50% 60%, #0000 80%)',
      webkitMask: 'linear-gradient(#0000 30%, #000 50% 60%, #0000 80%)'
    },
    {
      id: 7,
      blurPower: 6,
      mask: 'linear-gradient(#0000 40%, #000 60% 70%, #0000 90%)',
      webkitMask: 'linear-gradient(#0000 40%, #000 60% 70%, #0000 90%)'
    },
    {
      id: 8,
      blurPower: 7,
      mask: 'linear-gradient(#0000 50%, #000 70% 80%, #0000 100%)',
      webkitMask: 'linear-gradient(#0000 50%, #000 70% 80%, #0000 100%)'
    },
    {
      id: 9,
      blurPower: 8,
      mask: 'linear-gradient(#0000 60%, #000 80% 90%, #0000 100%)',
      webkitMask: 'linear-gradient(#0000 60%, #000 80% 90%, #0000 100%)'
    },
    {
      id: 10,
      blurPower: 9,
      mask: 'linear-gradient(#0000 70%, #000 90%, #0000 100%)',
      webkitMask: 'linear-gradient(#0000 70%, #000 90%, #0000 100%)'
    }
  ]

  const calculateBlur = (power: number) => {
    if (power === 0) return `var(--blur)`
    const ratioMultiplier = Array(power).fill('var(--ratio)').join(' * ')
    return `calc(var(--blur) / (${ratioMultiplier}))`
  }


  const baseClasses = 'pointer-events-none w-full z-[99] left-0 right-0 overflow-clip'
  

  const defaultClasses = 'fixed bottom-0 left-0 right-0 h-24   rotate-180'
  

  const finalClassName = overrideDefaults
    ? cn(baseClasses, className) 
    : cn(baseClasses, defaultClasses, className) 

  return (
    <div 
      className={finalClassName}
      style={{
        '--blur': blur,
        '--ratio': ratio,
      } as React.CSSProperties}
    >
      {panels.map((panel) => (
        <div
          key={panel.id}
          className="absolute w-full h-full p-0"
          style={{
            WebkitBackdropFilter: `blur(${calculateBlur(panel.blurPower)})`,
            backdropFilter: `blur(${calculateBlur(panel.blurPower)})`,
            WebkitMask: panel.webkitMask,
            mask: panel.mask,
          }}
        />
      ))}
    </div>
  )
}

export default ProgressiveBlur
